import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useI18n } from "@/i18n";
import { User, Building2, Loader2, CheckCircle2, XCircle, ShieldCheck, Receipt } from "lucide-react";

const field = "w-full bg-black/50 border border-white/10 text-white px-3 py-2.5 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none";

export const OnboardingDialog = () => {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const [type, setType] = useState("b2c");
  const [form, setForm] = useState({ company_name: "", vat_number: "", address_line: "", postal_code: "", city: "", country: "BE" });
  const [saving, setSaving] = useState(false);
  const [vies, setVies] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);

  const needs = !!user && user.role === "client" && !user.account_type;

  useEffect(() => {
    const reopen = () => setManualOpen(true);
    window.addEventListener("open-onboarding", reopen);
    return () => window.removeEventListener("open-onboarding", reopen);
  }, []);

  if (!needs) return null;
  // Never auto-open (no forced overlay/voile). The billing form opens only on demand.
  const open = manualOpen;

  const set = (k, v) => setForm((o) => ({ ...o, [k]: v }));

  const submit = async () => {
    if (type === "b2b" && (!form.company_name.trim() || !form.vat_number.trim())) {
      toast.error(t("onb.vatRequired"));
      return;
    }
    setSaving(true);
    setVies(null);
    try {
      const { data } = await api.post("/profile", { account_type: type, ...form });
      if (type === "b2b") {
        setVies(data.vies);
        if (data.vies?.valid) toast.success(`TVA / VAT (VIES) ✓ — ${data.vies.name || "OK"}`);
        else toast.warning("VIES ✗ — VAT 21%");
      } else {
        toast.success(t("onb.profileSaved"));
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  // Nothing auto-shows (no voile, no reminder pill). The billing form opens only
  // on demand via the "open-onboarding" event (e.g. a future profile/billing button).
  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setManualOpen(false); }}>
      <DialogContent className="popover-solid border-white/10 max-w-lg top-[2vh] translate-y-0 max-h-[96dvh] overflow-y-auto" data-testid="onboarding-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight">{t("onb.welcome")}</DialogTitle>
          <DialogDescription className="font-mono text-[11px] text-zinc-500">
            {t("onb.subtitle")}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 my-2">
          <button data-testid="onboarding-b2c" onClick={() => setType("b2c")}
            className={`flex flex-col items-center gap-2 p-4 rounded-sm border transition-colors ${type === "b2c" ? "border-aurora-cyan bg-aurora-cyan/10 text-aurora-cyan" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
            <User className="w-6 h-6" />
            <span className="font-display uppercase tracking-wide text-sm">{t("onb.particulier")}</span>
            <span className="font-mono text-[9px] opacity-70">{t("onb.b2cSub")}</span>
          </button>
          <button data-testid="onboarding-b2b" onClick={() => setType("b2b")}
            className={`flex flex-col items-center gap-2 p-4 rounded-sm border transition-colors ${type === "b2b" ? "border-aurora-cyan bg-aurora-cyan/10 text-aurora-cyan" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
            <Building2 className="w-6 h-6" />
            <span className="font-display uppercase tracking-wide text-sm">{t("onb.professionnel")}</span>
            <span className="font-mono text-[9px] opacity-70">{t("onb.b2bSub")}</span>
          </button>
        </div>

        <div className="space-y-2.5">
          {type === "b2b" && (
            <>
              <input data-testid="onboarding-company" className={field} placeholder={t("onb.company")} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} />
              <input data-testid="onboarding-vat" className={field} placeholder={t("onb.vat")} value={form.vat_number} onChange={(e) => set("vat_number", e.target.value.toUpperCase())} />
              {vies && (
                <div className={`flex items-center gap-2 font-mono text-[11px] ${vies.valid ? "text-signal-success" : "text-signal-error"}`} data-testid="vies-result">
                  {vies.valid ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  {vies.valid ? `VIES ✓${vies.name ? ` (${vies.name})` : ""}` : "VIES ✗ — VAT 21%"}
                </div>
              )}
            </>
          )}
          <input data-testid="onboarding-address" className={field} placeholder={t("onb.address")} value={form.address_line} onChange={(e) => set("address_line", e.target.value)} />
          <div className="grid grid-cols-3 gap-2.5">
            <input className={field} placeholder={t("onb.postal")} value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} />
            <input className={`${field} col-span-1`} placeholder={t("onb.city")} value={form.city} onChange={(e) => set("city", e.target.value)} />
            <input className={field} placeholder={t("onb.country")} value={form.country} onChange={(e) => set("country", e.target.value.toUpperCase())} />
          </div>
        </div>

        <button data-testid="onboarding-submit" onClick={submit} disabled={saving}
          className="mt-3 w-full flex items-center justify-center gap-2 py-3 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider rounded-sm disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          {type === "b2b" ? t("onb.validateVat") : t("onb.continue")}
        </button>
        <button data-testid="onboarding-skip" onClick={() => setManualOpen(false)} disabled={saving}
          className="mt-2 w-full py-2 text-center font-mono text-[11px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300 transition-colors disabled:opacity-60">
          Plus tard
        </button>
      </DialogContent>
    </Dialog>
  );
};
